// 개발요청 AI 종합 판단(P2b) — 상세 «② 검토» 카드 안 묶음(AX 만 · 가린 요청엔 없다 · ② 를 고치는 중에도 · 그리는 쪽은 DevRequestDetail).
//   서버 GET /api/devreq/requests/{id}/ai-report(P2a · require_ax) → { state, report, at, siblings_added, siblings_removed }.
//   state = none(판단 없음) · ready · stale(요청·답·기준·회차가 바뀌기 전 판단 — 판단은 그대로 보인다) · error(5번 실패 · 옛 판단이 있으면 같이).
//   report = { summary, recommend, priority, reasons[], concerns[], merge_with[](요청 번호), … } 또는 null.
// 🔴 결정은 사람이 한다 — «AI 권고대로 정하기» 같은 단추를 두지 않는다(묶음 안 단추 0 · 진행 여부 칸에 넣지 않는다).
// 🔴 role="status"·"alert"·"note" 와 <aside> 는 어떤 상태에서도 쓰지 않는다 — 목록 화면 시험은 status 가 다 사라질 때까지 기다리고,
//    ② 카드의 철회 안내(note)·상세(complementary)는 하나뿐이라고 단정한다.
// 상세를 다시 받으면(item 이 새 객체) 다시 부르되 부르는 동안 앞 판단을 그대로 둔다(깜빡임 없음) · 늦게 온 응답은 버린다(alive).
// 403·404 → 아무것도 안 그림 · 그 밖 실패 → 작은 회색 글 하나.
import { useEffect, useState } from 'react';
import { devreqAPI } from '../../api/client';
import { COLORS, TONES } from '../../utils/uiStyles';
import { fmtKstDateTime } from '../../utils/kstTime';
import { AI_REPORT_TEXT, aiReportSiblingsText, badge, decisionBadge } from './devreqLabels';

const STATES = ['none', 'ready', 'stale', 'error'];
const SILENT_STATUSES = [403, 404];
const isObj = (value) => !!value && typeof value === 'object' && !Array.isArray(value);
const str = (value) => (typeof value === 'string' ? value.trim() : '');
// 글자 목록 — 빈 글자·글자 아닌 값은 버린다(요청 번호가 수로 와도 글자로).
const strList = (value) => (Array.isArray(value) ? value : [])
  .map((x) => (typeof x === 'number' ? String(x) : str(x)))
  .filter(Boolean);
// 형제 차 — 서버는 정수(len)를 준다 · 0 이하·정수 아님은 0.
const count = (value) => (Number.isInteger(value) && value > 0 ? value : 0);

// 응답 → 화면 값 · 모르는 state 면 null(그리지 않는다).
function pick(data) {
  if (!isObj(data) || !STATES.includes(data.state)) return null;
  return {
    state: data.state,
    report: isObj(data.report) ? data.report : null,
    at: typeof data.at === 'string' ? data.at : null,
    added: count(data.siblings_added),
    removed: count(data.siblings_removed),
  };
}

const groupStyle = {
  margin: '0 14px 12px', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${COLORS.border}`,
  background: COLORS.surface, fontSize: '13px', color: COLORS.text, minWidth: 0, overflowWrap: 'anywhere',
};
const titleStyle = { fontSize: '12px', fontWeight: 700, color: COLORS.textSub, marginBottom: '6px' };
const labelStyle = { fontSize: '12px', fontWeight: 600, color: COLORS.textSub };
const subStyle = { fontSize: '12px', color: COLORS.textSub };
const NOTICE_COLORS = { none: COLORS.textSub, stale: COLORS.warn, error: COLORS.dangerText };
const PRIORITY_COLORS = [TONES.gray.soft, TONES.gray.ink];

function Items({ label, items }) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginTop: '6px' }}>
      <div style={labelStyle}>{label}</div>
      <ul aria-label={label} style={{ margin: '2px 0 0', paddingLeft: '18px' }}>
        {items.map((value, i) => <li key={`${i}-${value}`} style={{ whiteSpace: 'pre-wrap' }}>{value}</li>)}
      </ul>
    </div>
  );
}

// 판단 본문 — 값은 report 키 그대로 · 빈 목록은 줄째 없다.
function ReportBody({ report, at }) {
  const recommend = str(report.recommend);
  const priority = str(report.priority);
  const summary = str(report.summary);
  const mergeWith = strList(report.merge_with);
  const when = at ? fmtKstDateTime(at) : '';
  return (
    <>
      {(recommend || priority) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
          {recommend && <span style={decisionBadge(recommend)}>{`${AI_REPORT_TEXT.recommend} ${recommend}`}</span>}
          {priority && <span style={badge(PRIORITY_COLORS)}>{`${AI_REPORT_TEXT.priority} ${priority}`}</span>}
        </div>
      )}
      {summary && <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{summary}</p>}
      <Items label={AI_REPORT_TEXT.reasons} items={strList(report.reasons)} />
      <Items label={AI_REPORT_TEXT.concerns} items={strList(report.concerns)} />
      {mergeWith.length > 0 && <div style={{ marginTop: '6px' }}>{`${AI_REPORT_TEXT.mergeWith}: ${mergeWith.join(', ')}`}</div>}
      {when && <div style={{ ...subStyle, marginTop: '6px' }}>{`${AI_REPORT_TEXT.at} · ${when}`}</div>}
    </>
  );
}

// 바깥(훅 없음) — 목에 aiReport 가 없으면(기존 시험) 아무것도 그리지 않는다.
export default function DevRequestAiReport(props) {
  if (typeof devreqAPI?.aiReport !== 'function') return null;
  return <AiReportBlock {...props} />;
}

function AiReportBlock({ item }) {
  const [view, setView] = useState(undefined);   // undefined = 불러오는 중 · null = 그리지 않음(403·404·모르는 값) · 'failed' · 객체
  const id = item ? item.id : null;

  // item 이 새 객체면(상세를 다시 받음) 다시 부른다 — 앞 값은 그대로 두고, 앞 부름의 늦은 응답은 alive 로 버린다.
  useEffect(() => {
    if (id === null || id === undefined) return undefined;
    let alive = true;
    // 부르는 중 던져도 사슬 안이라 catch 로 간다.
    Promise.resolve()
      .then(() => devreqAPI.aiReport(id))
      .then((r) => { if (alive) setView(pick(r?.data)); })
      .catch((err) => { if (alive) setView(SILENT_STATUSES.includes(err?.response?.status) ? null : 'failed'); });
    return () => { alive = false; };
  }, [id, item]);

  if (!view) return null;
  if (view === 'failed') {
    return (
      <div role="group" aria-label={AI_REPORT_TEXT.title} style={groupStyle}>
        <div style={subStyle}>{AI_REPORT_TEXT.loadFailed}</div>
      </div>
    );
  }

  const { state, report, at, added, removed } = view;
  const hasReport = state !== 'none' && report !== null;
  // error 는 늘 실패 문구(옛 판단이 있으면 아래에 같이) · 판단이 없으면 «아직 … 없어요» · stale 은 판단 위에 한 줄.
  let notice = '';
  let tone = 'none';
  if (state === 'error') {
    notice = AI_REPORT_TEXT.error;
    tone = 'error';
  } else if (!hasReport) {
    notice = AI_REPORT_TEXT.none;
  } else if (state === 'stale') {
    notice = AI_REPORT_TEXT.stale;
    tone = 'stale';
  }
  return (
    <div role="group" aria-label={AI_REPORT_TEXT.title} data-ai-report-state={state} style={groupStyle}>
      <div style={titleStyle}>{AI_REPORT_TEXT.title}</div>
      {notice && <div style={{ fontSize: '12px', color: NOTICE_COLORS[tone], marginBottom: hasReport ? '6px' : 0 }}>{notice}</div>}
      {hasReport && <ReportBody report={report} at={at} />}
      {added + removed > 0 && <div style={{ ...subStyle, marginTop: '6px' }}>{aiReportSiblingsText(added, removed)}</div>}
    </div>
  );
}
